import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";

import { useAlert } from "react-alert";

import _ from "lodash";
import {
  createSourceFunction,
  fetchFunctionDefinitions,
} from "../actions/functions";
import { RefreshIcon } from "../components/icons/themes/windows10";
import { getDefaultValuesFromConfigFields } from "../destinations/modals/ConfigureIntegrationModal";
import { ModalField } from "../destinations/modals/ConfigureIntegrationModal";
import { FunctionRow } from "./FunctionRow";
import { FunctionTextInput } from "./FunctionTextInput";
import { useFunctionStyles } from "./styles";
import { FunctionDefinition, SourceFunction } from "./types";

export const NewSourceFunctionBuilder = () => {
  const classes = useFunctionStyles();
  const history = useHistory();
  const alert = useAlert();
  const dispatch = useDispatch();
  const [sourceName, setSourceName] = useState("");
  const [
    selectedFunctionSource,
    setSelectedFunctionSource,
  ] = useState<FunctionDefinition | null>(null);
  const { sourceFunctions: allSourceFunctions } = useSelector(
    (state) => (state as any).functionDefs
  );

  const [isSaving, setIsSaving] = useState(false);
  const { functionDefs, isLoaded } = useSelector(
    (state) => (state as any).functionDefs
  );
  const [settings, setSettings] = useState({});

  const handleChangeValues = (newValues: { [key: string]: any }) => {
    setSettings(
      _(settings)
        .extend(newValues)
        // Remove any values that are just a blank string as that's a filler value
        // we use from the backend.
        .pickBy((value) => value !== "")
        .value()
    );
  };

  const onSave = () => {
    if (!selectedFunctionSource) {
      return;
    }

    const newSourceFunction = {
      id: uuidv4(),
      name: sourceName,
      function_id: selectedFunctionSource.id,
      settings: settings,
    };

    if (newSourceFunction.name.length == 0) {
      return alert.error("Source function must have a name");
    }

    if (
      allSourceFunctions.find(
        (otherSourceFunction: SourceFunction) =>
          otherSourceFunction.name === newSourceFunction.name &&
          otherSourceFunction.id !== newSourceFunction.id
      )
    ) {
      return alert.error(
        "Source function has the same name as another source function"
      );
    }

    setIsSaving(true);

    (dispatch(createSourceFunction(newSourceFunction)) as any)
      .then((err: Error | void) => {
        if (err) {
          alert.error(err.message);
          console.log(err);
        } else {
          alert.success("Source Function Saved");
          history.push(`/source-functions/${newSourceFunction.id}`);
        }
      })
      .catch((error: any) => {
        alert.error("Could not save source function");
        console.log(error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  useEffect(() => {
    (dispatch(fetchFunctionDefinitions()) as any).catch((error: any) => {
      alert.error("Something went wrong. Unable to load page.");
      console.log(error);
      Sentry.captureException(error);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedFunctionDefs = useMemo(
    () =>
      _(functionDefs)
        .filter((functionDef) => functionDef.type === "SOURCE")
        .sortBy((functionDef) => functionDef.name.toLowerCase())
        .value(),
    [functionDefs]
  );

  const rows = sortedFunctionDefs.map((functionDef) => (
    <FunctionRow
      key={functionDef.id}
      onSelectFunction={() => setSelectedFunctionSource(functionDef)}
      functionDef={functionDef}
    />
  ));

  return (
    <Grid
      container
      spacing={0}
      alignItems="flex-start"
      alignContent="flex-start"
      className={clsx(classes.widgets, "px-5 pb-2")}
    >
      <Grid item xs={11}>
        <div className={classes.widget}>
          <Card>
            <CardHeader
              title={
                selectedFunctionSource
                  ? `New Source Function - ${selectedFunctionSource.name}`
                  : "New Source Function"
              }
              classes={{ action: clsx(classes.closeIconAction) }}
              action={
                <Button
                  variant="outlined"
                  onClick={() => history.replace("/source-functions")}
                >
                  Cancel
                </Button>
              }
            />
            {selectedFunctionSource ? (
              <>
                <Divider />
                <CardContent>
                  <FunctionTextInput
                    label="Source Name *"
                    key="name"
                    name="name"
                    value={sourceName}
                    onChangeText={setSourceName}
                  />
                  {selectedFunctionSource.config &&
                    selectedFunctionSource.config.configFields && (
                      <SourceSettingsConfig
                        functionDef={selectedFunctionSource}
                        onUpdateSettings={handleChangeValues}
                      />
                    )}
                </CardContent>
                <Divider />
                <CardActions className={classes.actions}>
                  {isSaving && <RefreshIcon className={clsx("animate-spin")} />}
                  <Button
                    className={clsx(classes.createFunctionButton, {
                      "animate-pulse": isSaving,
                    })}
                    onClick={onSave}
                    variant="outlined"
                    disabled={isSaving}
                  >
                    {!isSaving ? "CREATE SOURCE" : "CREATING SOURCE..."}
                  </Button>
                </CardActions>
              </>
            ) : (
              <>
                <CardContent>
                  <p>Select a function to use as a source</p>
                </CardContent>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Function Source</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{rows}</TableBody>
                </Table>
              </>
            )}
          </Card>
        </div>
      </Grid>
    </Grid>
  );
};

interface SourceSettingsConfigProps {
  functionDef: FunctionDefinition;
  settings?: { [key: string]: any };
  onUpdateSettings: (settings: { [key: string]: any }) => void;
}

const SourceSettingsConfig: React.FC<SourceSettingsConfigProps> = ({
  functionDef,
  settings = {},
  onUpdateSettings,
}) => {
  const configFields = functionDef.config!.configFields!;

  const defaultValueMap = getDefaultValuesFromConfigFields(configFields);

  const nonSensitiveSettings = settings
    ? Object.fromEntries(
        Object.keys(settings)
          .filter((fieldKey) => {
            const fieldConfig = configFields.find((f) => f.key === fieldKey);
            if (!fieldConfig) {
              return false;
            }
            return !fieldConfig.sensitive;
          })
          .map((fieldKey) => [fieldKey, settings[fieldKey]])
      )
    : [];

  const [values, setValues] = useState({
    ...defaultValueMap,
    ...nonSensitiveSettings,
  });

  const handleFieldChange = (key: string, value: any) => {
    const newValues = {
      ...defaultValueMap,
      ...values,
      [key]: value,
    };

    setValues(newValues);

    onUpdateSettings(newValues);
  };

  return (
    <>
      {configFields.map((field, i) => {
        return (
          <ModalField
            key={i}
            field={field}
            values={values}
            settings={settings}
            handleFieldChange={handleFieldChange}
            checkBoxEnabled={true}
            CustomConfigControlComponent={undefined}
          />
        );
      })}
    </>
  );
};
